import { coreActions } from '@sonar-software/sonar-core';
import { getRowKey } from '../';
import { PERISCOPE_TAB_TYPES } from '@sonar-software/sonar-ts-constants';
import { queryParamsToDynamicQueryLanguageParams } from '@sonar-software/sonar-ts-lib';
import { SelectionTable } from '@ui/tables';
import { v3FieldSortOptions } from './FieldSortOptions';

import {
	ApiCodeToggleQueryParams,
	SearchFilterQueryParams,
	SortByFilterQueryParams,
	TagFilterQueryParams
} from '../filters';
import {
	DynamicQueryOperator,
	KeyPaginationQueryParams,
	OffsetPaginatedResource,
	PeriscopeSfField,
	SonarObjectType
} from '@sonar-software/sonar-ts-types';
import { KeyPagination, keyPaginationDefaultQueryParams, keyPaginationFallbackQueryParams } from '@ui/KeyPagination';
import { PeriscopeFieldRow, SalesforceFieldSearch } from './';
import { QueryResourceApiTransform, useAction, useQueryResource, useSelectionTable } from '@hooks';
import React, { useCallback, useEffect, useRef } from 'react';

type Resource = OffsetPaginatedResource<PeriscopeSfField>;
export type SalesforceFieldSearchableSelectionTableV3QueryParams = ApiCodeToggleQueryParams &
	KeyPaginationQueryParams &
	SearchFilterQueryParams &
	SortByFilterQueryParams &
	TagFilterQueryParams;

type QueryParamsTransform = QueryResourceApiTransform<SalesforceFieldSearchableSelectionTableV3QueryParams>;

const dqlTransform: QueryParamsTransform = (params) =>
	 queryParamsToDynamicQueryLanguageParams(params, {
		capitalizeOrderingParams: false,
		filterParams: {
			filterByTags: { name: 'tagIds' },
			query: {
				name: params.useInternal ? 'InternalName' : 'DisplayName',
				operator: DynamicQueryOperator.Like
			}
		},
		orderingParams: {
			name: params.useInternal ? 'InternalName' : 'DisplayName'
		},
		removeParams: [ 'useInternal' ]
	});

export interface SalesforceFieldSearchableSelectionTableV3Props {
	parentIndex: number;
	resourceIdsUri?: string;
	showParents?: boolean;
	uri: string;
	useDql?: boolean;
}

export const SalesforceFieldSearchableSelectionTableV3: React.FC<SalesforceFieldSearchableSelectionTableV3Props> = ({
	parentIndex,
	resourceIdsUri,
	showParents,
	uri,
	useDql
}) => {
	const scrollableListRef = useRef<HTMLDivElement>(null);
	const updateReferences = useAction(coreActions.updateSonarObjectReferences);

	const { loading, resource, queryParams, updateParams } = useQueryResource<
		Resource,
		SalesforceFieldSearchableSelectionTableV3QueryParams
	>({
		defaultParams: {
			...keyPaginationDefaultQueryParams,
			sortBy: v3FieldSortOptions[ 1 ].key,
			sortDirection: v3FieldSortOptions[ 1 ].direction
		},
		fallbackParams: keyPaginationFallbackQueryParams,
		...useDql ? { apiTransform: dqlTransform } : {},
		uri
	});

	useEffect(() => {
		if (resource?.results) {
			updateReferences(
				resource.results.map((result) => ({
					displayName: result.displayName,
					ingestedBy: result.ingestedBy,
					parentDisplayName: result.parentDisplayName,
					parentSonarObjectId: result.parentSonarObjectId,
					populatedBy: result.populatedBy,
					relatedCounts: result.relatedCounts,
					sonarObjectId: result.sonarObjectId,
					sonarObjectType: SonarObjectType.SalesforceField,
					tags: result.tagIds
				}))
			);
		}
	}, [ resource?.results, updateReferences ]);

	const selectionData = useSelectionTable({
		getRowKey,
		queryParams: dqlTransform(queryParams),
		resourceIdsUri,
		results: resource?.results,
		selectionType: SonarObjectType.SalesforceField
	});

	const renderField = useCallback(
		(field: PeriscopeSfField) =>
			<PeriscopeFieldRow
				field={ field }
				key={ field.sonarObjectId }
				parentIndex={ parentIndex }
				showParents={ showParents }
				useApiNames={ Boolean(queryParams.useInternal) } />
		,
		[ parentIndex, queryParams.useInternal, showParents ]
	);

	const results = resource?.results;

	return (
		<>
			<SalesforceFieldSearch
				onChange={ updateParams }
				queryParams={ queryParams }
				showInternalNameToggle={ useDql } />
			<SelectionTable
				dataSource={ results }
				forceShowSelection={ resourceIdsUri ? undefined : false }
				loading={ loading }
				ref={ scrollableListRef }
				renderRow={ renderField }
				rowKey={ getRowKey }
				selectionData={ selectionData }
				selectionType={ PERISCOPE_TAB_TYPES.FIELDS } />
			<KeyPagination
				defaultPageSize={ Number(queryParams.count) }
				listRef={ scrollableListRef }
				onChange={ updateParams }
				resource={ resource } />
		</>
	);
};
